import is from '@sindresorhus/is';
import type Result from 'true-myth/result';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type {
    RealtimeEventListener,
    RealtimeEventListenerBuilder,
} from '~/shared/realtime-event-listener/realtime-event-listener';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    AgentChatMessagePage,
    CallbackRequest,
    ChatStreamEvent,
    CompleteEvent,
    ContextRefreshEvent,
    ErrorEvent,
    MessageStartEvent,
    StructuredResponseEvent,
    Thread,
    ThreadList,
    UserMessage,
} from '../../data-contract/chat';
import {
    agentChatMessagePageSchema,
    completeEventSchema,
    contextRefreshEventSchema,
    errorEventSchema,
    messageStartEventSchema,
    structuredResponseEventSchema,
    threadListSchema,
    threadSchema,
} from '../../data-contract/chat';

const CHAT_BASE = '/v1/agent/chat';

export type ListThreadMessagesParams = {
    readonly before?: string;
    readonly limit?: number;
};

export type SendMessageParams = {
    readonly body: UserMessage;
    readonly lastEventId?: string;
    readonly threadId: string;
};

export type ChatStreamListener = {
    readonly chunk: (text: string) => void;
    readonly complete: (event: CompleteEvent) => void;
    readonly contextRefresh: (event: ContextRefreshEvent) => void;
    readonly error: (event: ErrorEvent | Error) => void;
    readonly messageStart: (event: MessageStartEvent) => void;
    readonly structuredResponse: (event: StructuredResponseEvent) => void;
};

export type ChatStream = {
    readonly start: () => void;
    readonly stop: () => void;
};

export type ChatFetchers = {
    readonly createChatThread: () => Promise<Result<Thread, HttpFetcherError>>;
    readonly deleteChatThread: (threadId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchChatThread: (threadId: string) => Promise<Result<Thread, HttpFetcherError>>;
    readonly fetchChatThreadMessages: (
        threadId: string,
        params?: ListThreadMessagesParams,
    ) => Promise<Result<AgentChatMessagePage, HttpFetcherError>>;
    readonly fetchChatThreads: () => Promise<Result<ThreadList, HttpFetcherError>>;
    readonly sendChatMessage: (params: SendMessageParams, listeners: Partial<ChatStreamListener>) => ChatStream;
    readonly submitChatInteractionCallback: (args: {
        readonly blockId: string;
        readonly eventId: string;
        readonly payload: CallbackRequest;
    }) => Promise<Result<unknown, HttpFetcherError>>;
};

export type ChatFetchersDependencies = {
    readonly realtimeApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

type EnvelopeSchema<T extends ChatStreamEvent> = {
    readonly safeParse: (value: unknown) => { readonly data?: T; readonly success: boolean };
};

const parseEnvelope = <T extends ChatStreamEvent>(schema: EnvelopeSchema<T>, raw: string): T | null => {
    try {
        const parsed = JSON.parse(raw);
        const result = schema.safeParse(parsed);

        if (result.success === true && !is.undefined(result.data)) {
            return result.data;
        }

        return null;
    } catch {
        return null;
    }
};

const attachListeners = (listener: RealtimeEventListener, listeners: Partial<ChatStreamListener>): void => {
    listener.addEventListener('message-start', (event: MessageEvent) => {
        if (is.undefined(listeners.messageStart)) return;
        const envelope = parseEnvelope(messageStartEventSchema, event.data);
        if (envelope !== null) listeners.messageStart(envelope);
    });

    listener.addEventListener('chunk', (event: MessageEvent) => {
        if (is.undefined(listeners.chunk)) return;
        listeners.chunk(is.string(event.data) ? event.data : String(event.data));
    });

    listener.addEventListener('structured-response', (event: MessageEvent) => {
        if (is.undefined(listeners.structuredResponse)) return;
        const envelope = parseEnvelope(structuredResponseEventSchema, event.data);
        if (envelope !== null) listeners.structuredResponse(envelope);
    });

    listener.addEventListener('context-refresh', (event: MessageEvent) => {
        if (is.undefined(listeners.contextRefresh)) return;
        const envelope = parseEnvelope(contextRefreshEventSchema, event.data);
        if (envelope !== null) listeners.contextRefresh(envelope);
    });

    listener.addEventListener('complete', (event: MessageEvent) => {
        if (is.undefined(listeners.complete)) return;
        const envelope = parseEnvelope(completeEventSchema, event.data);
        if (envelope !== null) listeners.complete(envelope);
    });

    listener.addEventListener('error', (event: MessageEvent) => {
        if (is.undefined(listeners.error)) return;
        const envelope = parseEnvelope(errorEventSchema, event.data);
        if (envelope === null) {
            listeners.error(new Error('Unknown chat stream error'));
            return;
        }
        listeners.error(envelope);
    });

    listener.addEventListener('close', () => {
        if (is.undefined(listeners.error)) return;
        listeners.error(new Error('Chat stream closed'));
    });
};

export const createChatFetchers = (dependencies: ChatFetchersDependencies): ChatFetchers => {
    const { realtimeApiFetcher, realtimeEventListenerBuilder, validatingRealtimeApiFetcher } = dependencies;

    const fetchChatThreads: ChatFetchers['fetchChatThreads'] = () =>
        validatingRealtimeApiFetcher.get(threadListSchema, { pathname: `${CHAT_BASE}/threads` });

    const createChatThread: ChatFetchers['createChatThread'] = () =>
        validatingRealtimeApiFetcher.postJson(threadSchema, {
            pathname: `${CHAT_BASE}/threads`,
            payload: {},
        });

    const fetchChatThread: ChatFetchers['fetchChatThread'] = (threadId) =>
        validatingRealtimeApiFetcher.get(threadSchema, {
            pathname: `${CHAT_BASE}/threads/${threadId}`,
        });

    const deleteChatThread: ChatFetchers['deleteChatThread'] = (threadId) =>
        realtimeApiFetcher.delete({
            pathname: `${CHAT_BASE}/threads/${threadId}`,
        });

    const fetchChatThreadMessages: ChatFetchers['fetchChatThreadMessages'] = (threadId, params) => {
        const searchParams: Record<string, number | string> = {};
        if (!is.undefined(params?.limit)) searchParams['limit'] = params.limit;
        if (!is.undefined(params?.before)) searchParams['before'] = params.before;

        return validatingRealtimeApiFetcher.get(agentChatMessagePageSchema, {
            pathname: `${CHAT_BASE}/threads/${threadId}/messages`,
            requestOptions: is.emptyObject(searchParams) ? {} : { searchParams },
        });
    };

    const sendChatMessage: ChatFetchers['sendChatMessage'] = (params, listeners) => {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (!is.undefined(params.lastEventId)) {
            headers['Last-Event-ID'] = params.lastEventId;
        }

        const listener = realtimeEventListenerBuilder.build({
            autoReconnect: false,
            method: 'POST',
            path: `${CHAT_BASE}/threads/${params.threadId}/messages`,
            payload: JSON.stringify(params.body),
            requestHeaders: headers,
        });

        attachListeners(listener, listeners);

        return {
            start: () => listener.start(),
            stop: () => listener.stop(),
        };
    };

    const submitChatInteractionCallback: ChatFetchers['submitChatInteractionCallback'] = ({
        blockId,
        eventId,
        payload,
    }) =>
        realtimeApiFetcher.putJson({
            pathname: `${CHAT_BASE}/interactions/${eventId}/${blockId}`,
            payload,
        });

    return {
        createChatThread,
        deleteChatThread,
        fetchChatThread,
        fetchChatThreadMessages,
        fetchChatThreads,
        sendChatMessage,
        submitChatInteractionCallback,
    };
};
